import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, Observable, Subscription } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import * as fromSpaces from '../../../stores/spaces';
import { SpacesDispensesHD } from '../../interfaces/IHDspace';
import { ApiErrorHandlerService } from '../../services/api-error-handler/api-error-handler.service';
import {
  ACTIONS_LOCKER_SPACES,
  DISPENSE_STATES,
  INTERMEDIATE_STATES,
  SPACE_ACTION_EVENT,
  SPACE_STATES,
  TOAST_TYPES,
  VIEW_CALLING,
  VIEW_TYPES,
} from '../../utils/constants';
import { SocketService } from '../socket/socket.service';

@Injectable({
  providedIn: 'root',
})
export class LockerActionDataService {
  subscription: Subscription = new Subscription();
  state: RouterStateSnapshot;
  spaces$ = this.store.select(fromSpaces.selectSpaces);
  private currentSpaceInfo: SpacesDispensesHD;
  private spacesStructures: Array<object>;

  private spaceResetGoodStateMessage = 'common.spaceResetGoodStateMessage';
  private spaceResetDisableStateMessage = 'common.spaceResetDisableStateMessage';
  private spaceResetInitialStateMessage = 'common.spaceResetInitialStateMessage';
  orderUnableToVerify = 'common.orderUnableToVerify';
  spaceUnableToVerify = 'common.spaceUnableToVerify';

  isVerifyButtonDisabled$: Observable<boolean> = this.spaces$.pipe(
    map((spaces: SpacesDispensesHD[]) => {
      const hasLoadedVerify = spaces.some((space) =>
        space.dispenses.some((dispense) => dispense.state === DISPENSE_STATES.LOADED_VERIFY)
      );
      return !hasLoadedVerify;
    })
  );

  constructor(
    private router: Router,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private translateService: TranslateService,
    private store: Store,
    private socketService: SocketService
  ) {
    this.currentSpaceInfo = {} as SpacesDispensesHD;
    this.state = this.router.routerState.snapshot;
    this.detectSpaceChanges();
  }

  detectSpaceChanges(): void {
    this.socketService.listen(SPACE_ACTION_EVENT, () => {
      this.store.dispatch(fromSpaces.loadSpaces());
    });
  }

  set selectSpaceInfo(selectSpaceInfo: SpacesDispensesHD) {
    this.currentSpaceInfo = selectSpaceInfo;
  }

  get selectSpaceInfo(): SpacesDispensesHD {
    return this.currentSpaceInfo;
  }

  set sortedSpaces(spacesStructures: Array<object>) {
    this.spacesStructures = spacesStructures;
  }

  get sortedSpaces(): Array<object> {
    return this.spacesStructures;
  }

  getDispenseTime(space): string {
    if (!space.dispenses.length) return '';
    const times = space.dispenses[0].updateTimes;
    const dispenseTime = times.ordered + (times.loadedVerify || times.loaded || times.loading);
    const startTime = new Date(dispenseTime);
    return this.dispenseTime(startTime);
  }

  dispenseTime(startTime: Date): string {
    const endTime = new Date();
    const timeDiff = (endTime.getTime() - startTime.getTime()) / 1000;

    const hours = this.getDispenseHours(timeDiff);
    const minutes = this.getDispenseMinutes(timeDiff);
    const seconds = this.getDispenseSeconds(timeDiff);

    return hours === '00' ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
  }

  getDispenseHours(timeDiff: number): string | number {
    const hours = Math.floor(timeDiff / 3600);
    return hours < 10 ? '0' + hours : hours;
  }

  getDispenseMinutes(timeDiff: number): string | number {
    const minutes = Math.floor(timeDiff / 60) % 60;
    return minutes < 10 ? '0' + minutes : minutes;
  }

  getDispenseSeconds(timeDiff: number): string | number {
    const seconds = Math.floor(timeDiff % 60);
    return seconds < 10 ? '0' + seconds : seconds;
  }

  getTimerClass(space: SpacesDispensesHD): string {
    return space.dispenses && space.dispenses.length ? 'timer__' + space.dispenses[0].state : '';
  }

  getOpenedDoorClass(space: SpacesDispensesHD, positions: Array<string>): string {
    return positions.includes(space.hardwareInfo.position) ? 'opened-door__number' : '';
  }

  isSpaceFaulty(space: SpacesDispensesHD): boolean {
    const spaceState = space?.state;

    return (
      spaceState?.selectable !== SPACE_STATES.DISABLE &&
      (spaceState?.presence === SPACE_STATES.FILL ||
        spaceState?.secure === SPACE_STATES.OPEN ||
        spaceState?.selectable === SPACE_STATES.RESERVE)
    );
  }

  selectSpace(space: SpacesDispensesHD, url: string, actionSpace: string): void {
    if (!space.id) return;
    const hasOrder = space.dispenses.length > 0;
    const dispenseState = space.dispenses[0]?.state;
    const { position } = space.hardwareInfo;

    if (actionSpace === ACTIONS_LOCKER_SPACES.VERIFY && !hasOrder) {
      this.apiErrorHandlerService.showMessage(
        this.translateService.instant(this.spaceUnableToVerify, {
          label: position,
        }),
        TOAST_TYPES.Warning
      );
      return;
    }

    const ordersToRemove = [
      DISPENSE_STATES.LOADED,
      DISPENSE_STATES.LOADED_VERIFY,
      DISPENSE_STATES.LOCKEDDOWN,
    ].includes(dispenseState);

    if (ordersToRemove) {
      this.handleOrderAction(space, url, dispenseState, actionSpace);
    } else {
      this.handleSpaceAction(space, url, actionSpace);
    }
  }

  handleOrderAction(
    space: SpacesDispensesHD,
    url: string,
    dispenseState: string,
    actionSpace: string
  ): void {
    if (
      actionSpace === ACTIONS_LOCKER_SPACES.RESET ||
      actionSpace === ACTIONS_LOCKER_SPACES.SELECT
    ) {
      this.apiErrorHandlerService.showMessage(
        this.translateService.instant(this.spaceResetGoodStateMessage, {
          label: space.hardwareInfo.position,
          state: this.mappingStateMessage(dispenseState),
        }),
        TOAST_TYPES.Warning
      );
      return;
    }

    if (
      actionSpace === ACTIONS_LOCKER_SPACES.VERIFY &&
      dispenseState !== DISPENSE_STATES.LOADED_VERIFY
    ) {
      const { scheduleName } = space.dispenses[0].scheduleInfo;
      this.apiErrorHandlerService.showMessage(
        this.translateService.instant(this.orderUnableToVerify, {
          scheduleName,
          state: this.mappingStateMessage(dispenseState),
        }),
        TOAST_TYPES.Warning
      );
      return;
    }

    if (
      actionSpace === ACTIONS_LOCKER_SPACES.VERIFY &&
      dispenseState === DISPENSE_STATES.LOADED_VERIFY
    ) {
      this.navigate(url, VIEW_CALLING.VERIFY_ORDER, {});
      return;
    }

    const viewCalling =
      url === VIEW_TYPES.REAR_LOADING ? VIEW_CALLING.REMOVE_ITEM : VIEW_CALLING.RECLAIM_ORDER;

    this.navigate(url, viewCalling, {});
  }

  handleSpaceAction(space: SpacesDispensesHD, url: string, actionSpace: string): void {
    if (actionSpace === ACTIONS_LOCKER_SPACES.SELECT) {
      this.navigate(url, VIEW_CALLING.SELECTABLE_SPACE, {
        spaceSelectableState: space.state.selectable,
      });
    } else if (actionSpace === ACTIONS_LOCKER_SPACES.RESET) {
      this.handleResetSpace(space, url);
    } else if (space.state.secure === SPACE_STATES.CLOSE && url !== VIEW_TYPES.RETAIL) {
      this.navigate(url, VIEW_CALLING.OPEN_DOOR, {});
    } else if (url === VIEW_TYPES.RETAIL) {
      this.navigate(url, VIEW_CALLING.EMPTY_SPACE, {
        spaceSelectableState: space.state.selectable,
      });
    }
  }

  handleResetSpace(space: SpacesDispensesHD, url: string): void {
    const { position } = space.hardwareInfo;
    const { selectable, presence, secure } = space.state;

    if (selectable === SPACE_STATES.DISABLE) {
      this.apiErrorHandlerService.showMessage(
        this.translateService.instant(this.spaceResetDisableStateMessage, {
          label: position,
        }),
        TOAST_TYPES.Warning
      );
    } else if (
      !space.dispenses.length &&
      selectable === SPACE_STATES.AVAILABLE &&
      presence === SPACE_STATES.UNFILL &&
      secure === SPACE_STATES.CLOSE
    ) {
      this.apiErrorHandlerService.showMessage(
        this.translateService.instant(this.spaceResetInitialStateMessage, {
          label: position,
        }),
        TOAST_TYPES.Warning
      );
    } else {
      this.navigate(url, VIEW_CALLING.RESET_SPACE, {});
    }
  }

  navigate(url: string, viewCalling: string, params: object): void {
    const path = url === VIEW_TYPES.RETAIL ? `${url}/space-detail` : `${url}/confirmation`;
    this.router.navigate([`/${path}`, { viewCalling, ...params }]);
  }

  mappingStateMessage(state: string): string {
    const STATE_LOADED = DISPENSE_STATES.LOADED;
    const stateMessage = { [STATE_LOADED]: 'delivered' };
    return stateMessage[state] || state;
  }

  selectSpaceForAction(space: SpacesDispensesHD, actionSpace: string): void {
    const url = this.state.url.split('/')[1];
    this.selectSpaceInfo = space;
    if (space.dispenses && this.isSelectionSpaceDisabled(space.dispenses[0]?.state, actionSpace))
      return;
    this.selectSpace(space, url, actionSpace);
  }

  isSelectionSpaceDisabled(state: string, actionSpace: string): boolean {
    return (
      (!actionSpace || actionSpace === ACTIONS_LOCKER_SPACES.SELECT) &&
      INTERMEDIATE_STATES.includes(state)
    );
  }
}
